import { API_URL } from '@/config';
import { formatDateTime } from '@/utils/dateFormat';
import React, { useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Download, Upload, Database, RefreshCw, FileJson, AlertTriangle, Image, Package, FileUp, Server, Clock, Trash2, CloudDownload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${API_URL}/api`;

const AdminBackupPage = () => {
  const { user } = useOutletContext();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportingImages, setExportingImages] = useState(false);
  const [exportingComplete, setExportingComplete] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importingComplete, setImportingComplete] = useState(false);
  const [importingJson, setImportingJson] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [importData, setImportData] = useState('');
  const [serverBackups, setServerBackups] = useState([]);
  const [creatingServerBackup, setCreatingServerBackup] = useState(false);
  const [loadingServerBackups, setLoadingServerBackups] = useState(false);
  const fileInputRef = useRef(null);
  const completeFileInputRef = useRef(null);
  const jsonFileInputRef = useRef(null);

  useEffect(() => {
    loadStats();
    loadServerBackups();
  }, []);

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/backup/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadServerBackups = async () => {
    setLoadingServerBackups(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/backup/server-backups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setServerBackups(response.data.backups || []);
    } catch (error) {
      console.error('Failed to load server backups:', error);
    } finally {
      setLoadingServerBackups(false);
    }
  };

  const handleCreateServerBackup = async () => {
    setCreatingServerBackup(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.post(`${API}/backup/create-server-backup`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Backup created: ${response.data.filename} (${response.data.size_mb} MB)`);
      loadServerBackups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create backup');
    } finally {
      setCreatingServerBackup(false);
    }
  };

  const handleDownloadServerBackup = async (filename) => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/backup/download-server-backup/${filename}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Backup downloaded!');
    } catch (error) {
      toast.error('Failed to download backup');
    }
  };

  const handleDeleteServerBackup = async (filename) => {
    if (!confirm(`Delete backup ${filename}?`)) return;
    
    try {
      const token = localStorage.getItem('session_token');
      await axios.delete(`${API}/backup/delete-server-backup/${filename}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Backup deleted');
      loadServerBackups();
    } catch (error) {
      toast.error('Failed to delete backup');
    }
  };

  const handleRestoreServerBackup = async (filename) => {
    if (!confirm(`⚠️ This will restore all data from ${filename}. Existing data may be overwritten. Continue?`)) return;
    
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.post(`${API}/backup/restore-server-backup/${filename}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const results = response.data.results;
      toast.success(`Restored ${results.database.imported} records, ${results.images.extracted} images`);
      loadStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore backup');
    }
  };

  const handleExportAll = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/backup/export-all`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `lions_club_data_backup_${new Date().toISOString().slice(0,10)}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Data backup downloaded successfully!');
    } catch (error) {
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const handleExportImages = async () => {
    setExportingImages(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/backup/export-images`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `lions_club_images_${new Date().toISOString().slice(0,10)}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Images backup downloaded successfully!');
    } catch (error) {
      toast.error('Failed to export images');
    } finally {
      setExportingImages(false);
    }
  };

  const handleExportComplete = async () => {
    setExportingComplete(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/backup/export-complete`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `lions_club_COMPLETE_backup_${new Date().toISOString().slice(0,10)}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Complete backup (data + images) downloaded!');
    } catch (error) {
      toast.error('Failed to export complete backup');
    } finally {
      setExportingComplete(false);
    }
  };

  const handleImportImages = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.post(`${API}/backup/import-images`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success(`Images restored: ${response.data.extracted_count} files`);
      loadStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import images');
    }
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImportComplete = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImportingComplete(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.post(`${API}/backup/import-complete`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      const results = response.data.results;
      toast.success(`Restored: ${results.database.imported} records, ${results.images.extracted} images`);
      loadStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import complete backup');
    } finally {
      setImportingComplete(false);
      if (completeFileInputRef.current) {
        completeFileInputRef.current.value = '';
      }
    }
  };

  const handleImportJsonFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImportingJson(true);
    
    try {
      // Read the JSON file
      const text = await file.text();
      const parsed = JSON.parse(text);
      
      if (!parsed.collections) {
        toast.error('Invalid backup format. Missing "collections" field.');
        return;
      }

      const token = localStorage.getItem('session_token');
      const response = await axios.post(`${API}/backup/import`, parsed, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        maxContentLength: Infinity,
        maxBodyLength: Infinity
      });
      
      const results = response.data.results;
      const dbCount = Object.values(results.database || {}).reduce((sum, r) => sum + (r.imported || 0), 0);
      const imgCount = results.images?.restored || 0;
      
      toast.success(`Imported: ${dbCount} records, ${imgCount} images restored!`);
      setImportData('');
      loadStats();
    } catch (error) {
      if (error instanceof SyntaxError) {
        toast.error('Invalid JSON format');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to import data');
      }
    } finally {
      setImportingJson(false);
      if (jsonFileInputRef.current) {
        jsonFileInputRef.current.value = '';
      }
    }
  };

  const handleExportCollection = async (collection) => {
    try {
      const token = localStorage.getItem('session_token');
      const endpoint = collection === 'form_submissions' ? 'nominees' : collection;
      const response = await axios.get(`${API}/backup/export/${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const dataStr = JSON.stringify(response.data, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${collection}_backup_${new Date().toISOString().slice(0,10)}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`${collection} exported (${response.data.count} records)`);
    } catch (error) {
      toast.error(`Failed to export ${collection}`);
    }
  };

  const handleImport = async () => {
    if (!importData.trim()) {
      toast.error('Please paste backup JSON data');
      return;
    }

    try {
      const parsed = JSON.parse(importData);
      if (!parsed.collections) {
        toast.error('Invalid backup format. Missing "collections" field.');
        return;
      }

      setImporting(true);
      const token = localStorage.getItem('session_token');
      const response = await axios.post(`${API}/backup/import`, parsed, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      toast.success('Data imported successfully!');
      setImportData('');
      loadStats();
    } catch (error) {
      if (error instanceof SyntaxError) {
        toast.error('Invalid JSON format');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to import data');
      }
    } finally {
      setImporting(false);
    }
  };

  const handleClearData = async () => {
    const confirmText = prompt(
      '⚠️ WARNING: This will DELETE ALL DATA (events, nominees, votes, forms, etc.)!\n\n' +
      'This action CANNOT be undone.\n\n' +
      'Type "DELETE ALL" to confirm:'
    );
    
    if (confirmText !== 'DELETE ALL') {
      toast.error('Clear operation cancelled');
      return;
    }

    setClearing(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.delete(`${API}/backup/clear-data`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('All data cleared successfully!');
      loadStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clear data');
    } finally {
      setClearing(false);
    }
  };

  if (user?.role !== 'Super Admin') {
    return (
      <div className="p-8 text-center">
        <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-yellow-500" />
        <h2 className="text-xl font-bold mb-2">Access Denied</h2>
        <p className="text-muted-foreground">Only Super Admins can access the backup feature.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-backup-page">
      <div>
        <h1 className="text-2xl font-bold" style={{ color: '#0F172A' }}>Database Backup</h1>
        <p className="text-sm text-muted-foreground">Export and import your data for safe keeping</p>
      </div>

      <Alert className="border-amber-200 bg-amber-50">
        <AlertTriangle className="w-4 h-4 text-amber-600" />
        <AlertDescription className="text-amber-800">
          <strong>Important:</strong> Always download a backup before redeployment. This preserves your events, members, nominees, and votes data.
        </AlertDescription>
      </Alert>

      {/* Stats */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Database className="w-5 h-5" />
            Current Database Stats
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="animate-pulse flex gap-4">
              {[1,2,3,4].map(i => (
                <div key={i} className="h-16 w-24 bg-gray-200 rounded"></div>
              ))}
            </div>
          ) : stats ? (
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4">
              {Object.entries(stats).map(([key, value]) => (
                <div key={key} className="text-center p-3 rounded-lg bg-gray-50">
                  <p className="text-2xl font-bold" style={{ color: '#D4AF37' }}>{value}</p>
                  <p className="text-xs text-muted-foreground capitalize">{key.replace('_', ' ')}</p>
                </div>
              ))}
            </div>
          ) : (
            <p>Unable to load stats</p>
          )}
          <Button variant="ghost" size="sm" onClick={loadStats} className="mt-4">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </CardContent>
      </Card>

      {/* Complete Backup Section - Most Important */}
      <Card className="border-2 border-green-500">
        <CardHeader className="bg-green-50">
          <CardTitle className="flex items-center gap-2 text-green-700">
            <Package className="w-6 h-6" />
            Complete Backup (Recommended)
          </CardTitle>
          <CardDescription>Download everything - database + all images in one ZIP file</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 pt-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <Button 
              onClick={handleExportComplete} 
              disabled={exportingComplete}
              className="flex-1 bg-green-600 hover:bg-green-700"
              size="lg"
            >
              {exportingComplete ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                  Creating Backup...
                </span>
              ) : (
                <>
                  <Download className="w-5 h-5 mr-2" />
                  Download Complete Backup
                </>
              )}
            </Button>
            
            <div className="flex-1">
              <input
                type="file"
                accept=".zip"
                onChange={handleImportComplete}
                ref={completeFileInputRef}
                className="hidden"
                id="complete-import"
              />
              <Button
                variant="outline"
                size="lg"
                className="w-full border-green-500 text-green-700 hover:bg-green-50"
                onClick={() => completeFileInputRef.current?.click()}
                disabled={importingComplete}
              >
                {importingComplete ? (
                  <span className="flex items-center gap-2">
                    <div className="animate-spin h-4 w-4 border-2 border-green-600 border-t-transparent rounded-full"></div>
                    Restoring...
                  </span>
                ) : (
                  <>
                    <Upload className="w-5 h-5 mr-2" />
                    Restore Complete Backup
                  </>
                )}
              </Button>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            This ZIP contains: database_backup.json + uploads/ folder with all images
          </p>
        </CardContent>
      </Card>

      {/* Server-Side Automatic Backups */}
      <Card className="border-2 border-blue-500">
        <CardHeader className="bg-blue-50">
          <CardTitle className="flex items-center gap-2 text-blue-700">
            <Server className="w-6 h-6" />
            Server-Side Backups (Automatic)
          </CardTitle>
          <CardDescription>Automatic daily backups stored on the server. Keeps last 7 backups.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 pt-4">
          <div className="flex gap-4">
            <Button 
              onClick={handleCreateServerBackup} 
              disabled={creatingServerBackup}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {creatingServerBackup ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                  Creating...
                </span>
              ) : (
                <>
                  <CloudDownload className="w-4 h-4 mr-2" />
                  Create Backup Now
                </>
              )}
            </Button>
            <Button variant="outline" onClick={loadServerBackups} disabled={loadingServerBackups}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loadingServerBackups ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {serverBackups.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Filename</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {serverBackups.map((backup) => (
                  <TableRow key={backup.filename}>
                    <TableCell className="font-mono text-sm">{backup.filename}</TableCell>
                    <TableCell>{backup.size_mb} MB</TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1 text-sm">
                        <Clock className="w-3 h-3" />
                        {formatDateTime(backup.created_at)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-2 justify-end">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleDownloadServerBackup(backup.filename)}
                        >
                          <Download className="w-3 h-3" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleRestoreServerBackup(backup.filename)}
                        >
                          <Upload className="w-3 h-3" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => handleDeleteServerBackup(backup.filename)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Server className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No server-side backups yet. Click "Create Backup Now" to create one.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Export Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="w-5 h-5 text-blue-600" />
            Export Data (Separate Files)
          </CardTitle>
          <CardDescription>Download database and images separately</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Button 
              onClick={handleExportAll} 
              disabled={exporting}
              variant="outline"
              size="lg"
            >
              {exporting ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full"></div>
                  Exporting...
                </span>
              ) : (
                <>
                  <FileJson className="w-5 h-5 mr-2" />
                  Download Database (JSON)
                </>
              )}
            </Button>
            
            <Button 
              onClick={handleExportImages} 
              disabled={exportingImages}
              variant="outline"
              size="lg"
            >
              {exportingImages ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full"></div>
                  Exporting...
                </span>
              ) : (
                <>
                  <Image className="w-5 h-5 mr-2" />
                  Download Images (ZIP)
                </>
              )}
            </Button>
          </div>
          
          <div className="border-t pt-4">
            <p className="text-sm text-muted-foreground mb-3">Export individual collections:</p>
            <div className="flex flex-wrap gap-2">
              {['events', 'members', 'nominees', 'votes', 'forms'].map(col => (
                <Button
                  key={col}
                  variant="outline"
                  size="sm"
                  onClick={() => handleExportCollection(col)}
                >
                  <Download className="w-3 h-3 mr-1" />
                  {col}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Danger Zone - Clear Data */}
      <Card className="border-2 border-red-500">
        <CardHeader className="bg-red-50">
          <CardTitle className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="w-6 h-6" />
            Danger Zone - Clear All Data
          </CardTitle>
          <CardDescription className="text-red-600">
            Use this BEFORE importing a backup to remove test/old data. This will delete all events, nominees, votes, forms, etc.
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          <Button 
            onClick={handleClearData} 
            disabled={clearing}
            variant="destructive"
            className="bg-red-600 hover:bg-red-700"
          >
            {clearing ? (
              <span className="flex items-center gap-2">
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                Clearing...
              </span>
            ) : (
              <>
                <AlertTriangle className="w-4 h-4 mr-2" />
                Clear All Data
              </>
            )}
          </Button>
          <p className="text-xs text-red-600 mt-2">
            ⚠️ This will NOT delete admin users. You will need to type "DELETE ALL" to confirm.
          </p>
        </CardContent>
      </Card>

      {/* Import Images Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5 text-purple-600" />
            Import Images Only
          </CardTitle>
          <CardDescription>Restore images from a ZIP file</CardDescription>
        </CardHeader>
        <CardContent>
          <input
            type="file"
            accept=".zip"
            onChange={handleImportImages}
            ref={fileInputRef}
            className="hidden"
            id="image-import"
          />
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
          >
            <Image className="w-4 h-4 mr-2" />
            Select Images ZIP to Import
          </Button>
        </CardContent>
      </Card>

      {/* Import JSON Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5 text-blue-600" />
            Import from Live Backup (JSON with Images)
          </CardTitle>
          <CardDescription>
            Upload the JSON backup file exported from your live site (includes embedded images)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
            <p className="text-sm text-blue-800 mb-3">
              <strong>Recommended:</strong> Upload the JSON file directly (supports large files with images)
            </p>
            <input
              type="file"
              accept=".json"
              onChange={handleImportJsonFile}
              ref={jsonFileInputRef}
              className="hidden"
              id="json-file-import"
            />
            <Button
              onClick={() => jsonFileInputRef.current?.click()}
              disabled={importingJson}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {importingJson ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                  Importing...
                </span>
              ) : (
                <>
                  <FileUp className="w-4 h-4 mr-2" />
                  Select JSON Backup File
                </>
              )}
            </Button>
          </div>
          
          <div className="border-t pt-4">
            <p className="text-sm text-muted-foreground mb-2">Or paste JSON data manually (for small backups):</p>
            <Textarea
              placeholder="Paste your backup JSON data here..."
              value={importData}
              onChange={(e) => setImportData(e.target.value)}
              rows={6}
              className="font-mono text-sm"
            />
            <div className="flex gap-2 mt-2">
              <Button
                onClick={handleImport}
                disabled={importing || !importData.trim()}
                variant="outline"
              >
                {importing ? (
                  <span className="flex items-center gap-2">
                    <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full"></div>
                    Importing...
                  </span>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Import Pasted Data
                  </>
                )}
              </Button>
              <Button variant="outline" onClick={() => setImportData('')}>
                Clear
              </Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Note: Import will update existing records and add new ones. Images embedded as base64 will be restored automatically.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminBackupPage;
